import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { postDataApi } from '@crema/utility/APIHooks';
import { Button, Card, Col, Form, Input, Row, Typography, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { first } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../../@crema/utility/AuthHooks';
import PhoneInput from '../../profile/UserProfile/PersonalInfo/PhoneInput';
import './index.style.less';

export const ObjectiveCard = ({
  onClick,
  currentValue,
  title,
  image,
  value,
}) => {
  return (
    <Card
      onClick={onClick}
      className={`objective-card ${
        currentValue?.includes?.(value) ? `objective-card-selected` : ''
      }`}>
      <Title className='objective-card__title' level={4}>
        {title}
      </Title>
    </Card>
  );
};

const ContactUs = () => {
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [objective, setObjective] = useState([]);
  const { isAuthenticated } = useAuthUser();
  const navigate = useNavigate();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const OBJECTIVES = [
    'START A MENTORING PROGRAM',
    'EMPOWER EMPLOYEES TO GIVE BACK THROUGH MENTORING',
    'SCALE MENTORING INITIATIVES',
    'ESTABLISH A MENTORING HUB',
  ];

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    const token = await executeRecaptcha('submitContact');
    setToken(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const onFinish = async (values) => {
    await handleReCaptchaVerify();

    const isValid = isValidPhoneNumber(values.phone);
    if (!isValid) {
      form.setFields([
        {
          name: ['phone'],
          errors: ['Invalid Contact Number'],
        },
      ]);
      form.scrollToField(['phone'], {
        block: 'center',
        behavior: 'smooth',
      });
      return;
    }

    if (!token) {
      console.log('Recaptcha token not available');
      return;
    }

    setIsLoading(true);
    postDataApi(
      'api-svc/v2/contacts',
      { ...values, objective: objective.join(', '), reCAPTCHAToken: token },
      {
        fetchSuccess: () => {
          setIsLoading(false);
          message.success(
            'Your message has been sent successfully, we will be in touch soon.',
            10,
          );
          form.resetFields();
          navigate('/home');
        },
        fetchError: () => {
          message.error(
            'There is unexpected error happened, please double check your message and retry.',
          );
          setIsLoading(false);
        },
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  const onFinishFailed = (errorInfo) => {
    if (errorInfo?.errorFields?.length > 0) {
      const field = first(errorInfo.errorFields);
      if (field) {
        form.scrollToField(field.name, {
          block: 'center',
          behavior: 'smooth',
        });

        if (field.name[0] === 'phone') {
          setTimeout(() => {
            document.getElementById('phone').scrollIntoView();
          }, 100);
        }
      }
    }
  };

  const onObjectiveChanged = (value) => {
    if (objective?.includes?.(value)) {
      setObjective((prev) => prev.filter((item) => item !== value));
    } else {
      setObjective((prev) => [...prev, value]);
    }
  };

  return (
    <div className='contact-us-page'>
      <Form
        className='contact-us-form'
        form={form}
        initialValues={{}}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout='vertical'>
        <h3 className='text-center'>
          <Typography
            className='text-secondary'
            style={{ fontSize: 20, fontWeight: '500' }}>
            WELCOME ONBOARD
          </Typography>
        </h3>
        <p className='text-center fw-500' style={{ fontSize: 24 }}>
          I want to...
        </p>
        <Row
          gutter={32}
          xs={24}
          md={{
            span: isAuthenticated ? 24 : 8,
            offset: isAuthenticated ? 0 : 8,
          }}>
          <Col
            xs={24}
            style={{
              justifyContent: 'center',
              alignContent: 'center',
              display: 'flex',
            }}>
            <Row gutter={16} style={{ maxWidth: 800 }}>
              {OBJECTIVES.map((item, index) => (
                <Col md={12} xs={24} className='mt-16' key={item}>
                  <ObjectiveCard
                    title={item}
                    onClick={() => onObjectiveChanged(item)}
                    currentValue={objective}
                    value={item}
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        <Row gutter={16}></Row>

        <br />
        <br />
        <p className='text-center fw-500' style={{ fontSize: 24 }}>
          You may select more than one.
        </p>
        <br />
        <br />
        <p className='fw-500' style={{ fontSize: 24 }}>
          Contact Information
        </p>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name='name'
              label='Name'
              rules={[
                { required: true, max: 255 },
                {
                  message: 'Name is not valid.',
                  pattern: new RegExp(`^[a-zA-Z ]*$`),
                },
              ]}>
              <Input placeholder='Enter name' />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name='organizationName'
              label='Organisation Name'
              rules={[{ required: true, max: 255 }]}>
              <Input placeholder='Enter organization name' />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name='email'
              label='Email Address'
              rules={[{ required: true, max: 255 }, { type: 'email' }]}>
              <Input placeholder='Enter email' />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <PhoneInput
              id='phone'
              requiredMark='*'
              name={['phone']}
              label={'Contact Number'}
              form={form}
              rules={[
                {
                  required: true,
                  message: 'Please enter your Contact Number!',
                },
              ]}
            />
          </Col>

          <Col xs={24} md={24}>
            <Form.Item
              name='note'
              label='How can we work together?'
              rules={[{ required: true, max: 5000 }]}>
              <Input.TextArea
                rows={5}
                placeholder='Let us know what you think we can together'
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}></Col>
        </Row>

        <Row gutter={16} className='contact-footer'>
          <Col
            xs={24}
            md={{
              span: 12,
              offset: 6,
            }}
            className='d-space-between'>
            <Button danger type='outline' loading={isLoading}>
              <LeftOutlined
                onClick={() => navigate(-1)}
                style={{ fontSize: 14 }}
              />
              Back
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              disabled={!objective?.length}
              loading={isLoading}>
              SUBMIT
              <RightOutlined style={{ fontSize: 14 }} />
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ContactUs;
